import { motion } from 'framer-motion';
import { Shield, CheckCircle, FileCheck, Lock, Award, AlertTriangle, Scale, RefreshCw } from 'lucide-react';

const TOMMY_IMAGE = "https://images.unsplash.com/photo-1592194996308-7b43878e84a6?w=150&h=150&fit=crop";

export default function TrustCenter() {
  const trustPillars = [
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-level security with encryption, rate limiting, and continuous monitoring.",
      features: [
        "256-bit SSL encryption",
        "Rate limiting protection",
        "Input sanitization",
        "Regular security audits"
      ]
    },
    {
      icon: FileCheck,
      title: "Quality Assurance",
      description: "Every House Agent output goes through our quality framework.",
      features: [
        "Professional-grade templates",
        "Structured output formats",
        "Quality scoring system",
        "Consistent deliverables"
      ]
    },
    {
      icon: Scale,
      title: "Legal Compliance",
      description: "Built with legal requirements in mind from day one.",
      features: [
        "Clear disclaimers on all legal docs",
        "Attorney review recommended",
        "DMCA compliant",
        "Privacy-first approach"
      ]
    },
    {
      icon: RefreshCw,
      title: "Reliability",
      description: "Built to scale with 99.9% uptime commitment.",
      features: [
        "Scalable infrastructure",
        "Background job processing",
        "Health monitoring",
        "Automatic failover"
      ]
    }
  ];

  const legalGuarantees = [
    {
      title: "LegalDraft Guarantee",
      items: [
        "All documents use professional legal templates",
        "Standard clauses included (severability, governing law, notices)",
        "Clear disclaimer on every document",
        "Unique document ID for tracking",
        "Template versioning for consistency"
      ]
    }
  ];

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="trust-center-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src={TOMMY_IMAGE} alt="Tommy" className="w-16 h-16 rounded-full border-2 border-cyan-500" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">Trust Center</h1>
          </div>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
            TripleTC is built on a foundation of security, quality, and reliability. 
            Here's how we ensure you can trust our platform.
          </p>
        </motion.div>

        {/* Trust Pillars */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {trustPillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8 hover:border-cyan-500/30 transition-colors"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <pillar.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white">{pillar.title}</h3>
              </div>
              <p className="text-zinc-400 mb-4">{pillar.description}</p>
              <ul className="space-y-2">
                {pillar.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* House Agent Quality Standards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">House Agent Quality Standards</h2>
          
          <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-8 h-8 text-yellow-400" />
              <h3 className="text-xl font-semibold text-white">Professional-Grade Templates</h3>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-zinc-900/50 rounded-xl p-6">
                <h4 className="text-white font-semibold mb-3">TTC LegalDraft</h4>
                <ul className="space-y-2 text-sm text-zinc-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Professional NDA templates
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Contractor agreement frameworks
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Standard legal clauses included
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Clear attorney review disclaimer
                  </li>
                </ul>
              </div>
              
              <div className="bg-zinc-900/50 rounded-xl p-6">
                <h4 className="text-white font-semibold mb-3">TTC ResearchBot</h4>
                <ul className="space-y-2 text-sm text-zinc-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Structured research format
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Executive summary included
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Actionable recommendations
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Limitations clearly stated
                  </li>
                </ul>
              </div>
              
              <div className="bg-zinc-900/50 rounded-xl p-6">
                <h4 className="text-white font-semibold mb-3">TTC SEOWriter</h4>
                <ul className="space-y-2 text-sm text-zinc-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Meta tags included
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Proper heading hierarchy
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Keyword optimization
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Link suggestions
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Legal Disclaimer Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8 mb-16"
        >
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-8 h-8 text-yellow-400" />
            <h2 className="text-2xl font-bold text-white">Important Disclaimers</h2>
          </div>
          
          <div className="space-y-4 text-zinc-300">
            <p>
              <strong className="text-white">AI Limitations:</strong> While our House Agents use professional-grade 
              templates and prompts, AI technology is inherently imperfect. All outputs should be reviewed 
              by a qualified human before use in any critical application.
            </p>
            <p>
              <strong className="text-white">Legal Documents:</strong> TTC LegalDraft produces templates only. 
              These documents are NOT legal advice and have not been reviewed by a licensed attorney. 
              Always consult with a qualified attorney before signing or relying on any legal document.
            </p>
            <p>
              <strong className="text-white">Not Professional Advice:</strong> Our AI agents do not provide 
              professional advice in any field (legal, medical, financial, etc.). Outputs are for 
              informational purposes and should be verified by appropriate professionals.
            </p>
          </div>
        </motion.div>

        {/* Security Certifications (Future) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold text-white mb-4">Security Commitment</h2>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            We're committed to maintaining the highest security standards. Our infrastructure 
            is continuously monitored and updated to protect your data.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6">
            <div className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2 rounded-full">
              <Lock className="w-5 h-5 text-green-400" />
              <span className="text-zinc-300">SSL Encrypted</span>
            </div>
            <div className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2 rounded-full">
              <Shield className="w-5 h-5 text-cyan-400" />
              <span className="text-zinc-300">Rate Limited</span>
            </div>
            <div className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-yellow-400" />
              <span className="text-zinc-300">Input Validated</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
